import _ from 'underscore';
import { RANGES, COMPARISON_RANGES } from './constants';
import { ComparedDateRangesParams, ComparedDateRanges } from '../types';

/**
 * Get the difference between two dates in number of days (date1-date2)
 * @param {Date} date1 - First date, date to subtract from
 * @param {Date} date2 - Second date, date to be subtracted
 * @returns {number} - the number of days, rounded to closest value
 */
function dayDiff(date1: Date, date2: Date): number {
  return Math.round((date1.getTime() - date2.getTime()) / 86400000);
}

/**
 * Gets the date ranges that can be selected in the compared comparison panel.
 * @param {ComparedDateRangesParams} params - Object containing date utilities, current date, i18n, and primary date range.
 * @returns {ComparedDateRanges} - The compared date ranges object.
 */
export function comparedDateRanges({
  putil,
  today,
  i18n,
  primaryRange,
  comparedCustomStart = new Date(primaryRange.start),
  comparedCustomEnd = new Date(primaryRange.end),
}: ComparedDateRangesParams): ComparedDateRanges {
  let previousStart: Date | null = null;
  let previousEnd: Date | null = null;
  const lastYearStart = putil.todayNYearsAgo(primaryRange.start, 1);
  const lastYearEnd = putil.todayNYearsAgo(primaryRange.end, 1);

  switch (primaryRange.key) {
    case RANGES.CUSTOM:
      previousEnd = putil.lastNDate(primaryRange.start, 1);
      previousStart = putil.lastNDate(previousEnd, dayDiff(primaryRange.end, primaryRange.start));
      break;
    case RANGES.TODAY:
    case RANGES.YESTERDAY:
      previousStart = putil.lastNDate(primaryRange.start, 1);
      previousEnd = putil.lastNDate(primaryRange.end, 1);
      break;
    case RANGES.THIS_WEEK_SUNDAY:
    case RANGES.THIS_WEEK_MONDAY:
    case RANGES.LAST_WEEK_MONDAY:
    case RANGES.LAST_WEEK_SUNDAY:
    case RANGES.LAST_SEVEN_DAYS:
      previousStart = putil.lastNDate(primaryRange.start, 7);
      previousEnd = putil.lastNDate(primaryRange.end, 7);
      break;
    case RANGES.LAST_FOURTEEN_DAYS:
      previousStart = putil.lastNDate(primaryRange.start, 14);
      previousEnd = putil.lastNDate(primaryRange.end, 14);
      break;
    case RANGES.LAST_THIRTY_DAYS:
      previousStart = putil.lastNDate(primaryRange.start, 30);
      previousEnd = putil.lastNDate(primaryRange.end, 30);
      break;
    case RANGES.THIS_MONTH:
      previousStart = putil.firstDateNMonthsAgo(primaryRange.start, 1);
      previousEnd = putil.lastNDate(previousStart, -primaryRange.end.getDate() + 1);
      if (previousEnd >= primaryRange.start) {
        previousEnd = putil.lastNDate(primaryRange.start, 1);
      }
      break;
    case RANGES.LAST_MONTH:
      previousStart = putil.firstDateNMonthsAgo(primaryRange.start, 1);
      previousEnd = putil.lastDateNMonthsAgo(primaryRange.end, 1);
      break;
    case RANGES.LAST_THREE_MONTHS:
      previousStart = putil.firstDateNMonthsAgo(primaryRange.start, 3);
      previousEnd = putil.lastDateNMonthsAgo(primaryRange.end, 3);
      break;
    case RANGES.LAST_SIX_MONTHS:
      previousStart = putil.firstDateNMonthsAgo(primaryRange.start, 6);
      previousEnd = putil.lastDateNMonthsAgo(primaryRange.end, 6);
      break;
    case RANGES.TWELVE_MONTHS_TO_DATE:
      previousStart = putil.todayNYearsAgo(primaryRange.start, 1);
      previousEnd = putil.lastNDate(putil.firstDateNYearsAgo(today), 1);
      break;
    case RANGES.THIS_YEAR:
      previousStart = putil.firstDateNYearsAgo(today, 1);
      previousEnd = putil.lastNDate(putil.firstDateNYearsAgo(today), 1);
      break;
    case RANGES.LAST_YEAR:
      previousStart = putil.firstDateNYearsAgo(today, 2);
      previousEnd = putil.lastNDate(putil.firstDateNYearsAgo(today, 1), 1);
      break;
    case RANGES.ALL_TIME:
    default:
      break;
  }

  const dateRanges: ComparedDateRanges = {
    [COMPARISON_RANGES.PREVIOUS_PERIOD]: {
      text: i18n.getString('DateFilter_Option_PreviousPeriod'),
      start: previousStart,
      end: previousEnd,
      invalid: primaryRange.key === RANGES.ALL_TIME,
    },
    [COMPARISON_RANGES.SAME_PERIOD_LAST_YEAR]: {
      text: i18n.getString('DateFilter_Option_SamePeriodLastYear'),
      start: lastYearStart,
      end: lastYearEnd,
      invalid: primaryRange.key === RANGES.ALL_TIME,
    },
    [COMPARISON_RANGES.CUSTOM]: {
      text: i18n.getString('DateFilter_Option_CustomPeriod'),
      start: comparedCustomStart,
      end: comparedCustomEnd,
      isCustom: true,
    },
  };

  // If "all time" is selected then mark the compared periods as invalid
  if (primaryRange.key === RANGES.ALL_TIME) {
    _.each(dateRanges, (range) => _.extend(range, { invalid: true }));
  }

  // Add the key to each date range
  _.each(dateRanges, (range, key) => _.extend(range, { key }));
  return dateRanges;
}
